// Who did the work — the artist picker (docs/APP.md → Artists, "The FORM").
//
// A shop that keeps a list of its artists in the dashboard sends that list to the form on
// `public_data.artists`, and its client picks a name off a list instead of typing one at a counter.
// A shop with NO list sees the field it has always seen and posts what it has always posted, down
// to the byte. That negative is the load-bearing half: six shops have thousands of printed QR codes
// pointing at their pages, 9,700 signed rows behind them, and bundles cached on phones we will never
// see. Nothing in this file fires unless a list actually arrives.
//
// WHAT GETS POSTED IS STILL `nameOfArtist`, THE ARTIST'S NAME. The API matches an artist by name —
// that is how every row since 2023 is keyed, and it is why a removed artist's old waivers still read
// correctly. `artistId` rides ALONGSIDE the name when the name came off the list, so a shop that
// later renames an artist can still tell whose row it was; a name typed into "Someone else" carries
// no id, because there is nothing true to say about it.
//
// The decisions live here rather than in Form.tsx so the ones that are pure — which options, which
// name gets posted, which id, what the picker starts on — have tests of their own (artists.test.ts).

import type { Lang } from "./i18n";

/** Which form an artist is listed on. Absent means both — every roster written before 2026-09-14 has
 *  no `does` at all, and nothing about those shops changes. */
export type ArtistDoes = "tattoo" | "piercing";
export const ARTIST_DOES: ArtistDoes[] = ["tattoo", "piercing"];

export type ArtistOption = { id: string; name: string; licenseNumber?: string; does?: ArtistDoes[] };

/** The last option in the list. A value no artist's name can be, because the select's value IS the
 *  name that gets posted and a sentinel that collides would post itself. */
export const SOMEONE_ELSE = "__someone_else__";
export const SOMEONE_ELSE_LABEL = "Someone else";
const SOMEONE_ELSE_LABEL_ES = "Otra persona";
export const someoneElseLabel = (lang: Lang = "en"): string =>
  lang === "es" ? SOMEONE_ELSE_LABEL_ES : SOMEONE_ELSE_LABEL;

/** The `does` an entry carries, or nothing: a list holding "tattoo" and/or "piercing", anything else
 *  in it ignored. An entry with no list — or a list with nothing recognisable in it — is on BOTH
 *  forms, which is what every artist was before the setting existed. */
export function artistDoes(entry: unknown): ArtistDoes[] | undefined {
  const raw = (entry as { does?: unknown } | null | undefined)?.does;
  if (!Array.isArray(raw)) return undefined;
  const does = ARTIST_DOES.filter((d) => raw.includes(d));
  return does.length ? does : undefined;
}

/** The shop's artists, as the picker shows them. Anything that is not a list of things with names
 *  is no list at all, and this shop keeps the field it has always had.
 *
 *  Two artists with the same name collapse to one option: the name is what gets posted, so showing
 *  it twice would be one choice offered twice with no way to tell them apart.
 *
 *  WITH A SERVICE (2026-09-14; Robert Jarrett, 2 Ton Gallery: "it would be nice if when on the body
 *  piercing form the tattoo artist names weren't an option to choose from"), only the artists listed
 *  on that form: an entry whose `does` names the service, or carries no `does` at all. A shop whose
 *  piercers are the only ones marked for piercings shows a client getting pierced just the piercers;
 *  a shop that marked nobody shows everyone on both, as it always did. No service (a `both` shop's
 *  client has not picked yet, and the field is not drawn until they do) is the whole roster.
 *
 *  A COSMETIC TATTOO (TW-0279) lists the artists marked for no form in particular: `does` cannot name cosmetic work yet
 *  (ARTIST_DOES is the shop app's two, until its slice), so an artist the shop marked tattoo-only or piercing-only is
 *  not offered on the cosmetic form, and "Someone else" takes any name. Never the tattoo list by fallback. */
export function artistOptions(publicData: unknown, service: "tattoo" | "piercing" | "cosmetic" | "" = ""): ArtistOption[] {
  const raw = (publicData as { artists?: unknown } | null | undefined)?.artists;
  if (!Array.isArray(raw)) return [];
  const out: ArtistOption[] = [];
  const seen = new Set<string>();
  for (const entry of raw) {
    const row = (entry ?? {}) as { id?: unknown; name?: unknown; license_number?: unknown };
    const name = `${row.name ?? ""}`.trim();
    if (!name || seen.has(name.toLowerCase())) continue;
    const does = artistDoes(entry);
    if (service && does && !(does as string[]).includes(service)) continue;
    seen.add(name.toLowerCase());
    const licenseNumber = `${row.license_number ?? ""}`.trim();
    out.push({ id: `${row.id ?? ""}`.trim(), name, ...(licenseNumber ? { licenseNumber } : {}), ...(does ? { does } : {}) });
  }
  return out;
}

/** Does this shop's client get a list to pick from? */
export const asksWhichArtist = (options: ArtistOption[]): boolean => options.length > 0;

/** ASK WHICH ARTIST, ASK WHICH PIERCER (TW-0422, 2026-10-22): the owner's own switch, one per service, in the shop app's Settings
 *  → Waiver form. A shop that does both in Kennewick WA (biggestlittleshopoffun), through the contact form: "we do bulk piercings
 *  so you can't request a piercer." Absent or true is the question this form has always asked, the picker or the plain box, byte
 *  for byte; a literal false takes the whole question off that service's page (neither the picker nor the box), and the body
 *  posts `nameOfArtist` as "" (the API's signing door wants the key there, and "" is a box left empty). One per service because a
 *  shop that does both is two forms at one counter, the physician switch's reason: the piercing form can stop asking while the
 *  tattoo form keeps asking. A cosmetic tattoo always asks.
 *
 *  THE STATE'S RULE WINS: where it puts the artist's signature or license number on every client record (the contract's
 *  `artist_signature`, `artist_license_number`), the question is asked whatever the row says (the API refuses the switch off
 *  there, and this holds for a row that said false before its state was set). */
export const ASK_ARTIST_SETTINGS = { tattoo: "ask_artist_tattoo", piercing: "ask_artist_piercing" } as const;
export const ARTIST_ON_RECORD_RULES = ["artist_signature", "artist_license_number"] as const;

export function asksArtistFor(publicData: unknown, service: string, required: ReadonlySet<string> = new Set()): boolean {
  if (service !== "tattoo" && service !== "piercing") return true;
  if (ARTIST_ON_RECORD_RULES.some((rule) => required.has(rule))) return true;
  return (publicData as Record<string, unknown> | null | undefined)?.[ASK_ARTIST_SETTINGS[service]] !== false;
}

/** The name that gets posted: the one picked off the list, or whatever was typed under
 *  "Someone else". An empty picker falls through to what was typed too, so a client who wrote a
 *  name before the shop's list arrived never loses it. */
export function artistNameFor(choice: string, typed: string): string {
  const picked = `${choice ?? ""}`.trim();
  if (!picked || picked === SOMEONE_ELSE) return `${typed ?? ""}`.trim();
  return picked;
}

/** The id to post beside the name, or nothing. "Someone else" is nobody on the list. */
export function artistIdFor(choice: string, options: ArtistOption[]): string {
  const picked = `${choice ?? ""}`.trim();
  if (!picked || picked === SOMEONE_ELSE) return "";
  return options.find((a) => a.name === picked)?.id ?? "";
}

/** What the picker is already on when it appears. The shop arrives a beat after the first paint
 *  (App.tsx races a static file against the API), so a client CAN have typed a name into the plain
 *  field before the list showed up: their name if it is on the list, "Someone else" with what they
 *  typed still in the box if it is not. Nothing typed, nothing chosen — there is deliberately no
 *  default artist, the same reason a `both` shop has no default service. */
export function initialChoice(options: ArtistOption[], typed: string): string {
  const name = `${typed ?? ""}`.trim();
  if (!name) return "";
  const hit = options.find((a) => a.name.toLowerCase() === name.toLowerCase());
  return hit ? hit.name : SOMEONE_ELSE;
}

/** The choice after the LIST changed under it — a `both` shop's client picked a tattoo, chose Robert,
 *  then switched to a piercing, and Robert is not on the piercing list. A name that is no longer
 *  offered is dropped (nothing chosen, the box empty), because a select whose value is not one of its
 *  options paints blank while the state still says Robert, and the form would post a piercer the
 *  client never picked. "Someone else" and a name still on the list stay where they are. Nothing
 *  chosen yet falls through to initialChoice, so a name typed before the list arrived keeps its place. */
export function choiceAfterListChange(was: string, options: ArtistOption[], typed: string): string {
  const picked = `${was ?? ""}`.trim();
  if (!picked) return initialChoice(options, typed);
  if (picked === SOMEONE_ELSE) return SOMEONE_ELSE;
  return options.some((a) => a.name === picked) ? picked : "";
}

/** The picker's own words. The form's own register, and the same tattoo/piercing split every other
 *  sentence on this page makes (service.ts → COPY). A cosmetic tattoo's is the artist's (TW-0279), the
 *  word its signed waiver prints ("Name of Artist"): only a piercing says piercer. */
export function pickerCopy(
  service: "tattoo" | "piercing" | "cosmetic" | "",
  lang: Lang = "en"
): { prompt: string; hint: string; missing: string } {
  if (lang === "es") {
    const quien = service === "piercing" ? "perforador" : "artista";
    return {
      prompt: "Elige una opción",
      hint: `Elige a tu ${quien}. ¿No está en la lista? Elige ${SOMEONE_ELSE_LABEL_ES}.`,
      missing: `Elige a tu ${quien} de la lista.`,
    };
  }
  const who = service === "piercing" ? "piercer" : "artist";
  return {
    prompt: "Choose one",
    hint: `Pick your ${who}. Not on the list? Choose ${SOMEONE_ELSE_LABEL}.`,
    missing: `Pick your ${who} from the list.`,
  };
}

/** The body the form posts, with the picker's answer folded in.
 *
 *  A shop with NO list gets back the very object it handed in — not a copy, not a copy with the
 *  same keys: the same object. That is the strongest way to say the thing that must stay true, and
 *  it is what artists.test.ts asserts. No `artistId` key appears, and `nameOfArtist` is whatever
 *  the client typed, exactly as it has been since 2023.
 *
 *  A shop WITH a list gets `nameOfArtist` set to the name that was picked (or typed under "Someone
 *  else"), and `artistId` beside it only when the name came off the list. */
export function withArtistPicked<T extends Record<string, any>>(
  body: T,
  options: ArtistOption[],
  choice: string,
): Record<string, any> {
  if (!asksWhichArtist(options)) return body;
  const out: Record<string, any> = { ...body, nameOfArtist: artistNameFor(choice, `${body.nameOfArtist ?? ""}`) };
  const id = artistIdFor(choice, options);
  if (id) out.artistId = id;
  return out;
}
